import _ from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Form } from 'antd';

import { ExpenseAction, ExpenseType, IExpense } from 'models';
import { GetAll, Create, Delete, Update, Get } from 'services/expense.service';
import { API_DATE_FORMAT } from 'utils/date';
import { Api } from '../../../services/api';
import { CarOutlined, SolutionOutlined, WalletOutlined } from '@ant-design/icons';

export interface ExpenseDefaultFilters {
  type?: ExpenseType;
  contact?: string;
  from?: moment.Moment;
  to?: moment.Moment;
}

export const defaultExpensesFilters: ExpenseDefaultFilters = {
  type: ExpenseType.All,
  contact: '-1',
  from: undefined,
  to: undefined,
};

interface FetchExpensesProps {
  defaultFilters?: ExpenseDefaultFilters;
  pageNumber?: number;
  pageSize?: number;
  type?: ExpenseType;
  contact?: string;
  from?: moment.Moment;
  to?: moment.Moment;
}

export function mapExpense(obj: IExpense) {
  let icon = <WalletOutlined style={{ marginRight: '4px' }} />;
  if (obj.expenseType === ExpenseType.Reclaimable) {
    icon = <SolutionOutlined style={{ marginRight: '4px' }} />;
  }
  if (obj.expenseType === ExpenseType.Kilometre) {
    icon = <CarOutlined style={{ marginRight: '4px' }} />;
  }
  return {
    ...obj,
    idAttachments: obj.attachments || [],
    date: moment(obj.date),
    icon,
    isGstFree: obj.amountGst === 0,
  };
}

export function useExpensesToInvoice() {
  const [loading, setLoading] = useState<boolean>();
  const [error, setError] = useState<string>();
  const [data, setData] = useState<IExpense[]>();

  const load = async (contact: string) => {
    setLoading(true);
    try {
      const rs = await GetAll({ contact, action: ExpenseAction.Invoice });
      setLoading(false);
      setData(rs.data.map(mapExpense));
    } catch (err) {
      setLoading(false);
      setError(err as any);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setData([]);
  };

  return { loading, data, error, load, reset };
}

export function useExpenses(props: FetchExpensesProps) {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>();
  const [data, setData] = useState<IExpense[]>();
  const [total, setTotal] = useState<number>();
  const [pagination, setPagination] = useState({ pageSize: 100, pageNumber: 1 });
  const [filterCounter, setFilterCounter] = useState(0);
  const [filter, setFilter] = useState<ExpenseDefaultFilters>({
    type: props.defaultFilters?.type,
    contact: props.defaultFilters?.contact,
    from: props.defaultFilters?.from,
    to: props.defaultFilters?.to,
  });

  const load = async () => {
    const from = props.from ?? filter.from?.format(API_DATE_FORMAT);
    const to = props.to ?? filter.to?.format(API_DATE_FORMAT);
    const contact = props.contact ?? filter.contact;

    const filters = {
      pageNumber: props.pageNumber ?? pagination.pageNumber,
      pageSize: props.pageSize ?? pagination.pageSize,
      type: props.type ?? filter.type,
      ...(contact ? { contact } : {}),
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
    };

    const diffType = !_.isEqual(defaultExpensesFilters.type, filters.type);
    const diffContact = !_.isEqual(defaultExpensesFilters.contact, filters.contact);
    const diffFrom = !_.isEqual(defaultExpensesFilters.from, filters.from);
    const diffTo = !_.isEqual(defaultExpensesFilters.to, filters.to);

    const counter = [diffType, diffContact, diffFrom, diffTo].filter(Boolean).length;

    setFilterCounter(counter);

    // Filter all should not be passed
    if (filters.type === ExpenseType.All) {
      delete filters.type;
    }
    if (filters.contact === '-1') {
      delete filters.contact;
    }

    setLoading(true);
    await Api.tryCatch(
      GetAll(filters),
      (rs) => {
        setLoading(false);
        setData(rs.data.map(mapExpense));
        setTotal(rs.meta.total);
      },
      (err) => {
        setLoading(false);
        setError(err.message);
      },
    );
  };

  let cancelToken = Api.getCancelTokenSource();
  useEffect(() => {
    cancelToken = Api.getCancelTokenSource();
    return () => {
      cancelToken.cancel();
    };
  }, []);

  useEffect(() => {
    load();
  }, [props.pageNumber, props.pageSize, props.type, props.contact, props.from, props.to, filter, pagination]);

  return { loading, error, data, total, pagination, setPagination, filterCounter, setFilter, reloadList: load };
}

export function useRemoveExpense() {
  const [isDeleting, setIsDeleting] = useState(false);

  const remove = async (id: string) => {
    setIsDeleting(true);
    try {
      await Delete(id);
    } finally {
      setIsDeleting(false);
    }
  };

  return { isDeleting, remove };
}

export function useSaveExpense() {
  const [isSaving, setIsSaving] = useState(false);
  const [form] = Form.useForm();

  const save = async (payload: IExpense) => {
    setIsSaving(true);
    try {
      if (payload.id) {
        const obj = await Update(payload.id, payload);
        return mapExpense(obj);
      }
      const obj = await Create(payload);
      return mapExpense(obj);
    } finally {
      setIsSaving(false);
    }
  };

  return { isSaving, save, form };
}

export function useExpense() {
  const [loading, setIsLoading] = useState(false);

  const load = async (id: string) => {
    setIsLoading(true);
    try {
      const obj = await Get(id);
      return mapExpense(obj);
    } finally {
      setIsLoading(false);
    }
  };

  return { loading, load };
}
